
import React, { useMemo } from 'react';
import { Split, RefreshCw, ShieldCheck, Link2, AlertTriangle, BrainCircuit } from 'lucide-react';
import type { SemanticMatchResult } from '../../../services';
import InlineCostBadge from '../../../components/commercial/InlineCostBadge';
import { usePreflightCost } from '../../../hooks/usePreflightCost';
import { isCommercial } from '../../../config/appMode';

export interface TranslationProgress {
    totalTranslated: number;
    totalSegments: number;
    completedBatches: number;
    totalBatches: number;
    percent: number;
}

export interface BatchProgress {
    batchIndex: number;
    totalBatches: number;
    status: 'pending' | 'running' | 'done' | 'error';
    translatedCount: number;
    totalSegments: number;
    errorMessage?: string;
}

interface Step3ReviewProps {
    sourceSegments: string[];
    aiOutputSegments: string[];
    humanSegments: string[];
    semanticMatchResult: SemanticMatchResult | null;
    matchedRules?: string;
    isTranslating: boolean;
    isAuditing: boolean;
    isMatching: boolean;
    hasTranslationError?: boolean;
    hasMatchError?: boolean;
    realtimeProgress?: TranslationProgress;
    realtimeBatches?: BatchProgress[];
    onStartAudit: () => void;
    onRunMatch: () => void;
    onRetranslate: () => void;
    onPreviewPrompt: () => void;
    retranslateCredits?: number | null;
}

const Step3Review: React.FC<Step3ReviewProps> = ({
    sourceSegments,
    aiOutputSegments,
    humanSegments,
    semanticMatchResult,
    isTranslating,
    isAuditing,
    isMatching,
    hasTranslationError = false,
    hasMatchError = false,
    realtimeProgress,
    realtimeBatches,
    onStartAudit,
    onRunMatch,
    onRetranslate,
    onPreviewPrompt,
    retranslateCredits
}) => {
    const maxRows = Math.max(sourceSegments?.length || 0, aiOutputSegments?.length || 0);
    const hasMatchResult = semanticMatchResult !== null && semanticMatchResult !== undefined;

    // Pre-flight cost for Begin Audit button — only fetch when match done + not auditing
    const auditPreflight = usePreflightCost({
        type: 'audit',
        sourceSegments,
        aiSegments: aiOutputSegments,
        humanSegments: hasMatchResult && semanticMatchResult?.alignedHumanSegments
            ? semanticMatchResult.alignedHumanSegments
            : humanSegments,
        enabled: hasMatchResult && !isAuditing && !isTranslating,
    });

    // Pre-flight cost for Run Match button — semantic-match (currently FREE markup=0, but show badge for future)
    const matchPreflight = usePreflightCost({
        type: 'semantic-match',
        sourceSegments,
        humanSegments,
        enabled: !isMatching && !isTranslating && sourceSegments.length > 0 && humanSegments.length > 0,
    });
    const hasUnmatched = hasMatchResult && (semanticMatchResult?.unmatchedCount || 0) > 0;
    // Block Audit if Translation or Match has an error
    const auditBlocked = hasTranslationError || hasMatchError || !hasMatchResult;

    // Use aligned human segments if match result exists, otherwise fall back to original order
    const getAlignedHuman = (idx: number): string => {
        if (hasMatchResult && semanticMatchResult?.alignedHumanSegments?.[idx]) {
            return semanticMatchResult.alignedHumanSegments[idx];
        }
        return humanSegments?.[idx] || '';
    };

    const isUnmatched = (idx: number): boolean => {
        if (!hasMatchResult || !semanticMatchResult?.mappings) return false;
        const mapping = semanticMatchResult.mappings.find(m => m.sourceIndex === idx);
        return mapping?.isUnmatched ?? false;
    };

    // Realtime progress from Supabase
    const streamingCount = realtimeProgress?.totalTranslated || 0;

    return (
        <div className="flex flex-col h-full animate-in fade-in duration-500 px-4 w-full max-w-[95%] mx-auto">
            {/* Header — clean Vietnamese */}
            <div className="mb-5 flex items-start justify-between gap-4 shrink-0">
                <div className="min-w-0">
                    <h2 className="text-2xl font-black text-slate-800 flex items-center gap-2 tracking-tight">
                        <Split className="w-6 h-6 text-amber-600" /> Bản dịch &amp; Đối chiếu
                    </h2>
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs">
                        <span className="text-slate-500 font-semibold">
                            {sourceSegments.length} đoạn · {isTranslating ? 'Đang dịch' : 'Sẵn sàng'}
                        </span>
                        {isTranslating && realtimeProgress !== undefined && (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-semibold">
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                {streamingCount === 0
                                    ? 'AI đang suy nghĩ...'
                                    : `${streamingCount}/${sourceSegments.length} đoạn`}
                            </span>
                        )}
                        {hasUnmatched && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 rounded font-semibold">
                                <AlertTriangle className="w-3 h-3" />
                                {semanticMatchResult?.unmatchedCount} chưa khớp
                            </span>
                        )}
                    </div>
                </div>

                {/* Action buttons — primary CTA right, secondary outline left */}
                <div className="flex gap-2 items-center shrink-0 flex-wrap justify-end">
                    {/* Tertiary: Copy */}
                    <button
                        onClick={() => navigator.clipboard.writeText(aiOutputSegments.join(' '))}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-colors whitespace-nowrap"
                    >
                        Sao chép
                    </button>

                    {/* Secondary: Re-translate */}
                    <button
                        onClick={onRetranslate}
                        disabled={isTranslating || isAuditing}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                        title={isTranslating ? 'Đang dịch...' : 'Dịch lại toàn bộ'}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${isTranslating ? 'animate-spin' : ''}`} />
                        {isTranslating ? 'Đang dịch...' : 'Dịch lại'}
                        {isCommercial && retranslateCredits != null && !isTranslating && (
                            <InlineCostBadge credits={retranslateCredits} />
                        )}
                    </button>

                    {/* Secondary: Re-match (Semantic match) */}
                    <button
                        onClick={onRunMatch}
                        disabled={isMatching || isTranslating}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap ${
                            hasMatchResult
                                ? 'text-violet-700 bg-violet-50 border-violet-200 hover:bg-violet-100'
                                : 'text-white bg-violet-600 border-violet-600 hover:bg-violet-700'
                        }`}
                    >
                        {isMatching ? <RefreshCw className="animate-spin w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                        {isMatching ? 'Đang đối chiếu...' : hasMatchResult ? 'Đối chiếu lại' : 'Đối chiếu'}
                        {!isMatching && (
                            <InlineCostBadge
                                credits={matchPreflight.credits}
                                isComputing={matchPreflight.isComputing}
                                error={matchPreflight.error}
                            />
                        )}
                    </button>

                    {/* Primary CTA: Begin Audit */}
                    <button
                        onClick={onStartAudit}
                        disabled={isTranslating || auditBlocked}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap shadow-sm ${
                            isAuditing
                                ? 'bg-amber-600 hover:bg-amber-700 text-white shadow-amber-200'
                                : auditBlocked
                                    ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-200'
                        }`}
                        title={hasTranslationError ? 'Bản dịch lỗi' : hasMatchError ? 'Đối chiếu lỗi' : !hasMatchResult ? 'Cần đối chiếu trước' : isAuditing ? 'Audit lại' : 'Bắt đầu Audit'}
                    >
                        {isAuditing ? <RefreshCw className="animate-spin w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                        {isAuditing ? 'Audit lại' : 'Bắt đầu Audit'}
                        {!isAuditing && !auditBlocked && (
                            <InlineCostBadge
                                credits={auditPreflight.credits}
                                isComputing={auditPreflight.isComputing}
                                error={auditPreflight.error}
                            />
                        )}
                    </button>
                </div>
            </div>

            <div className="flex-1 bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-lg flex flex-col">
                <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 shrink-0">
                    <div className="px-4 py-3 border-r border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Bản gốc (Tiếng Việt)
                    </div>
                    <div className="px-4 py-3 border-r border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                        {isTranslating && <RefreshCw className="w-3 h-3 animate-spin text-amber-600" />}
                        Bản dịch AI
                    </div>
                    <div className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
                        <Link2 className="w-3 h-3 text-emerald-600" />
                        Bản chuẩn{hasMatchResult ? ' (đã khớp)' : ''}
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">
                    {Array.from({ length: maxRows }).map((_, idx) => {
                        const unmatched = isUnmatched(idx);
                        return (
                            <div key={idx} className={`grid grid-cols-3 group hover:bg-slate-50 border-b border-slate-100 min-h-[80px] ${unmatched && hasMatchResult ? 'bg-red-50' : ''}`}>
                                <div className="p-4 border-r border-slate-100 relative">
                                    <span className="absolute top-4 left-2 text-[10px] font-mono text-slate-400">#{idx + 1}</span>
                                    <p className="text-sm text-slate-700 leading-relaxed pl-4 whitespace-pre-wrap">{sourceSegments[idx] || ""}</p>
                                </div>
                <div className="p-4 border-r border-indigo-100 bg-indigo-50">
                                    {(() => {
                                        const finalText = aiOutputSegments[idx];
                                        const displayText = finalText || '';
                                        if (displayText) {
                                            return (
                                                <p className={`text-sm leading-relaxed whitespace-pre-wrap font-medium text-indigo-800`}>
                                                    {displayText}
                                                </p>
                                            );
                                        }
                                        if (isTranslating) {
                                            return <p className="text-xs text-indigo-400 animate-pulse">...</p>;
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div className="p-4">
                                    <p className={`text-sm leading-relaxed whitespace-pre-wrap ${unmatched && hasMatchResult ? 'text-red-400 italic' : 'text-emerald-700'}`}>
                                        {hasMatchResult ? getAlignedHuman(idx) : humanSegments[idx] || ''}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Step3Review;
